import set from 'lodash/set';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateComponent } from '~/app/components/dynamic-grid/dynamic-grid-helpers';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import { usePopulateValues } from './use-populate-values';

export const fontFamilyOptions = [
  { label: 'Default', value: 'Default' },
  { label: 'Chronicle', value: 'Chronicle' },
  { label: 'Obviously', value: 'Obviously' },
  { label: 'Gotham Black', value: 'GothamBlack' },
];

type UseDynamicTextProps = {
  fieldPath?: string;
  interpolationSource?: string[];
  textEditorElementRef?: React.RefObject<HTMLTextAreaElement>;
};

type StylingTag = 'b' | 'i' | 'u' | 's';

// This function wraps the selected text with the given tag
// It returns the formatted text and updates the last tags selection
const wrapSelectionWithTag = (
  tag: string,
  textAreaRef?: React.RefObject<HTMLTextAreaElement>,
  lastTagsSelection?: { start: string; end: string }
) => {
  const textSelection = textAreaRef?.current;
  if (!textSelection) return '';

  const start = textSelection.selectionStart;
  const end = textSelection.selectionEnd;
  const selectedText = textSelection.value.substring(start, end);

  if (!selectedText) return textSelection.value;

  const before = textSelection.value.substring(0, start);
  const after = textSelection.value.substring(end);

  const openTag = `<${tag}>`;
  const closeTag = `</${tag}>`;

  const isWrapped =
    selectedText.startsWith(openTag) && selectedText.endsWith(closeTag);

  let formattedText = '';

  let newSelectionStart = start;
  let newSelectionEnd = end + openTag.length + closeTag.length;

  // wait for the ui lifecycle to update before setting the selection
  requestAnimationFrame(() => {
    textAreaRef.current?.setSelectionRange(newSelectionStart, newSelectionEnd);
    textAreaRef.current?.focus();
  });

  if (isWrapped) {
    // Toggle off: unwrap the tag
    const unwrapped = selectedText.slice(
      openTag.length,
      selectedText.length - closeTag.length
    );

    newSelectionStart = start;
    newSelectionEnd = end - openTag.length - closeTag.length;
    formattedText = `${before}${unwrapped}${after}`;
    return formattedText;
  }

  // Toggle on: wrap the tag
  formattedText = `${before}${openTag}${selectedText}${closeTag}${after}`;
  if (lastTagsSelection) {
    lastTagsSelection.start = openTag;
    lastTagsSelection.end = closeTag;
  }

  return formattedText;
};

const lastTagsSelection = { start: '', end: '' };

type TextState = {
  text: string;
  interpolationSource: string[];
  fontSize: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  textAlign: 'left' | 'center' | 'right' | 'justify';
  color: string;
  hexColor: string;
  marketplaceOfferId: string;
  pressReleaseId: string;
  fontFamily: string;
  lineHeight: string;
  letterSpacing: string;
  defaultEmptyText: string;
  mobile: {
    fontSize: number;
    lineHeight: string;
  };
  tablet: {
    fontSize: number;
    lineHeight: string;
  };
  interpolatedLinkItems: {
    impressionSection: string;
    key: string;
    value: string;
    impressionPosition: string;
  }[];
  enableApplyNowButtonTracking: boolean;
  captureCustomQueryParams: boolean;
};

const initialState: TextState = {
  text: '',
  interpolationSource: ['marketplaceOffer'],
  fontSize: 16,
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  textAlign: 'left',
  color: '#000000',
  hexColor: '#000000',
  pressReleaseId: '',
  marketplaceOfferId: '',
  fontFamily: 'Default',
  lineHeight: 'normal',
  letterSpacing: 'normal',
  defaultEmptyText: '',
  mobile: {
    fontSize: 16,
    lineHeight: 'normal',
  },
  tablet: {
    fontSize: 16,
    lineHeight: 'normal',
  },
  interpolatedLinkItems: [],
  enableApplyNowButtonTracking: false,
  captureCustomQueryParams: false,
};

const fieldsToPopulate = [
  { fieldKey: 'text', initialValue: '' },
  { fieldKey: 'fontSize', initialValue: 16 },
  { fieldKey: 'bold', initialValue: false },
  { fieldKey: 'italic', initialValue: false },
  { fieldKey: 'underline', initialValue: false },
  { fieldKey: 'strikethrough', initialValue: false },
  { fieldKey: 'textAlign', initialValue: 'left' },
  { fieldKey: 'color', initialValue: '#000000' },
  { fieldKey: 'hexColor', initialValue: '#000000' },
  { fieldKey: 'lineHeight', initialValue: 'normal' },
  { fieldKey: 'letterSpacing', initialValue: 'normal' },
  { fieldKey: 'marketplaceOfferId', initialValue: '' },
  { fieldKey: 'fontFamily', initialValue: 'Default' },
  { fieldKey: 'mobile.fontSize', initialValue: 16 },
  { fieldKey: 'mobile.lineHeight', initialValue: 'normal' },
  { fieldKey: 'mobile', initialValue: '#000000' },
  { fieldKey: 'tablet.fontSize', initialValue: 16 },
  { fieldKey: 'tablet.lineHeight', initialValue: 'normal' },
  { fieldKey: 'tablet.color', initialValue: '#000000' },
  { fieldKey: 'interpolationSource', initialValue: ['marketplaceOffer'] },
  { fieldKey: 'pressReleaseId', initialValue: '' },
  { fieldKey: 'defaultEmptyText', initialValue: '' },
  { fieldKey: 'enableApplyNowButtonTracking', initialValue: false },
  { fieldKey: 'captureCustomQueryParams', initialValue: false },
  {
    fieldKey: 'interpolatedLinkItems',
    initialValue: [
      { key: '', value: '', impressionSection: '', impressionPosition: '' },
    ],
  },
];

export const useDynamicText = ({
  fieldPath = '',
}: UseDynamicTextProps = {}) => {
  const dispatch = useDispatch();
  const { componentPath } = useDynamicGrid();
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const { marketplaceOffers, pressReleases } = useDynamicGrid({
    componentId: selectedComponentId,
  });
  const currentComponent = flatOutline?.[selectedComponentId];
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [state, setState] = useState<TextState>(initialState);

  const handleUpdateComponent = useCallback(
    (field: string, value: any) => {
      const fieldPathValue = fieldPath
        ? `props.${componentPath}.${fieldPath}.${field}` // If fieldPath is provided, update the nested field
        : `props.${componentPath}.${field}`; // If fieldPath is not provided, update the top-level field
      set(currentComponent, fieldPathValue, value);
      dispatch(updateComponent(currentComponent));
    },
    [currentComponent, componentPath, fieldPath, dispatch]
  );

  const handleFormat = useCallback(
    (
      tag: StylingTag,
      handleUpdateComponentFn: VoidFunction,
      field: keyof TextState
    ) => {
      if (!textAreaRef?.current) return;
      const { selectionStart, selectionEnd } = textAreaRef?.current;

      const hasSelection = selectionStart !== selectionEnd;
      if (hasSelection) {
        const formattedText =
          wrapSelectionWithTag(tag, textAreaRef, lastTagsSelection) ||
          state.text;
        handleUpdateComponent('text', formattedText);
        setState((prev) => ({ ...prev, text: formattedText }));
        return;
      }
      handleUpdateComponentFn();
      setState((prev) => ({ ...prev, [field]: !prev[field] }));
    },
    [handleUpdateComponent, state.text, textAreaRef]
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    handleUpdateComponent('text', e.target.value);
    setState((prev) => ({ ...prev, text: e.target.value }));
  };

  const handleInterpolationSourceChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const val = e.target.value as unknown as string[];
    handleUpdateComponent('interpolationSource', val);
    setState((prev) => ({ ...prev, interpolationSource: val }));
  };

  const handleChangeHexColor = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('hexColor', e.target.value);
    setState((prev) => ({ ...prev, hexColor: e.target.value }));
  };

  // Formatting text handlers

  const handleClickBold = () => {
    handleFormat('b', () => handleUpdateComponent('bold', !state.bold), 'bold');
  };

  const handleClickItalic = () => {
    handleFormat(
      'i',
      () => handleUpdateComponent('italic', !state.italic),
      'italic'
    );
  };

  const handleClickUnderline = () => {
    handleFormat(
      'u',
      () => handleUpdateComponent('underline', !state.underline),
      'underline'
    );
  };

  const handleClickStrikethrough = () => {
    handleFormat(
      's',
      () => handleUpdateComponent('strikethrough', !state.strikethrough),
      'strikethrough'
    );
  };

  // end of formatting text handlers

  const handleClickTextAlign = (
    align: 'left' | 'center' | 'right' | 'justify'
  ) => {
    handleUpdateComponent('textAlign', align);
    setState((prev) => ({ ...prev, textAlign: align }));
  };

  const handleChangeFontSize = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('fontSize', Number(e.target.value));
    setState((prev) => ({ ...prev, fontSize: Number(e.target.value) }));
  };

  const handleChangeColor = (colorValue: string) => {
    handleUpdateComponent('color', colorValue);
    setState((prev) => ({ ...prev, color: colorValue }));
  };

  const handleChangeFontFamily = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleUpdateComponent('fontFamily', e.target.value);
    setState((prev) => ({ ...prev, fontFamily: e.target.value }));
  };

  const handleChangeLineHeight = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('lineHeight', e.target.value);
    setState((prev) => ({ ...prev, lineHeight: e.target.value }));
  };

  const handleChangeLetterSpacing = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('letterSpacing', e.target.value);
    setState((prev) => ({ ...prev, letterSpacing: e.target.value }));
  };

  const handleChangeMobileFontSize = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('mobile.fontSize', Number(e.target.value));
    setState((prev) => ({
      ...prev,
      mobile: { ...prev.mobile, fontSize: Number(e.target.value) },
    }));
  };

  const handleChangeTabletFontSize = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('tablet.fontSize', Number(e.target.value));
    setState((prev) => ({
      ...prev,
      tablet: { ...prev.tablet, fontSize: Number(e.target.value) },
    }));
  };

  const handleChangeMobileLineHeight = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('mobile.lineHeight', e.target.value);
    setState((prev) => ({
      ...prev,
      mobile: { ...prev.mobile, lineHeight: e.target.value },
    }));
  };

  const handleChangeTabletLineHeight = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('tablet.lineHeight', e.target.value);
    setState((prev) => ({
      ...prev,
      tablet: { ...prev.tablet, lineHeight: e.target.value },
    }));
  };

  const handleAddInterpolatedLink = () => {
    setState((prev) => ({
      ...prev,
      interpolatedLinkItems: [
        ...prev.interpolatedLinkItems,
        { key: '', value: '', impressionSection: '', impressionPosition: '' },
      ],
    }));
    handleUpdateComponent('interpolatedLinkItems', [
      ...state.interpolatedLinkItems,
      { key: '', value: '', impressionSection: '', impressionPosition: '' },
    ]);
  };

  const handleChangeInterpolatedLinkValue = (
    index: number,
    value: string,
    type: 'key' | 'value' | 'impressionSection' | 'impressionPosition'
  ) => {
    const newItems = [...state.interpolatedLinkItems];
    if (type === 'key') {
      newItems[index].key = value;
    }

    if (type === 'value') {
      newItems[index].value = value;
    }

    if (type === 'impressionSection') {
      newItems[index].impressionSection = value;
    }

    if (type === 'impressionPosition') {
      newItems[index].impressionPosition = value;
    }
    setState((prev) => ({ ...prev, interpolatedLinkItems: newItems }));
    handleUpdateComponent('interpolatedLinkItems', newItems);
  };

  const handleDeleteInterpolatedLink = (index: number) => {
    if (state.interpolatedLinkItems.length < 2) {
      return;
    }
    const newItems = [...state.interpolatedLinkItems];
    newItems.splice(index, 1);
    setState((prev) => ({ ...prev, interpolatedLinkItems: newItems }));
    handleUpdateComponent('interpolatedLinkItems', newItems);
  };

  const handleEnableApplyNowButtonTrackingChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('enableApplyNowButtonTracking', e.target.checked);
    setState((prev) => ({
      ...prev,
      enableApplyNowButtonTracking: e.target.checked,
    }));
  };

  const handleCaptureCustomQueryParamsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('captureCustomQueryParams', e.target.checked);
    setState((prev) => ({
      ...prev,
      captureCustomQueryParams: e.target.checked,
    }));
  };

  const handleChangeDefaultEmptyText = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleUpdateComponent('defaultEmptyText', e.target.value);
    setState((prev) => ({ ...prev, defaultEmptyText: e.target.value }));
  };

  const textInterpolationsOptions = useCallback(() => {
    return [
      { label: 'Marketplace Offer', value: 'marketplaceOffer' },
      { label: 'Press Releases', value: 'pressRelease' },
      { label: 'GraphQL Query', value: 'graphQlQuery' },
      { label: 'Interpolated Link', value: 'interpolatedLink' },
    ];
  }, []);

  const pressReleaseOptions = useCallback(() => {
    const options = pressReleases?.map((release: any, index: number) => ({
      label: release.title,
      value: index,
    }));
    options.unshift({
      label: 'Select a Press Release',
      value: '',
    });
    return options;
  }, [pressReleases]);

  const marketplaceOfferOptions = useCallback(() => {
    const options = marketplaceOffers?.map((offer: any) => ({
      label: offer.name,
      value: offer.id,
    }));
    options.unshift({
      label: 'Select a Marketplace Offer',
      value: '',
    });
    return options;
  }, [marketplaceOffers]);

  const handleMarketplaceOfferChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value: selectedOfferId } = e.target;
      handleUpdateComponent('marketplaceOfferId', selectedOfferId);
      setState((prev) => ({ ...prev, marketplaceOfferId: selectedOfferId }));
    },
    [handleUpdateComponent]
  );

  const handlePressReleaseChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value: selectedReleaseId } = e.target;
      handleUpdateComponent('pressReleaseId', selectedReleaseId);
      setState((prev) => ({ ...prev, pressReleaseId: selectedReleaseId }));
    },
    [handleUpdateComponent]
  );

  const selectedMarketplaceOffer = useMemo(() => {
    return marketplaceOffers?.find(
      (offer: any) => offer.id === state.marketplaceOfferId
    );
  }, [marketplaceOffers, state.marketplaceOfferId]);

  usePopulateValues({
    fieldPath,
    setState,
    fields: fieldsToPopulate,
  });

  return {
    ...state,
    handleChange,
    handleInterpolationSourceChange,
    handleClickBold,
    handleClickItalic,
    handleClickUnderline,
    handleClickStrikethrough,
    handleClickTextAlign,
    handleChangeFontSize,
    handleChangeColor,
    handleUpdateComponent,
    textInterpolationsOptions,
    pressReleaseOptions,
    marketplaceOfferOptions,
    handleMarketplaceOfferChange,
    handlePressReleaseChange,
    selectedMarketplaceOffer,
    handleChangeFontFamily,
    textAreaRef,
    handleChangeHexColor,
    handleChangeLineHeight,
    handleChangeLetterSpacing,
    handleChangeMobileFontSize,
    handleChangeTabletFontSize,
    handleChangeMobileLineHeight,
    handleChangeTabletLineHeight,
    handleAddInterpolatedLink,
    handleDeleteInterpolatedLink,
    handleChangeInterpolatedLinkValue,
    handleChangeDefaultEmptyText,
    handleEnableApplyNowButtonTrackingChange,
    handleCaptureCustomQueryParamsChange,
  };
};
